import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { contactInfo } from '../mock/reviewsData';

const footerSections = {
  Products: [
    { label: 'Health Insurance', to: '/insurance/health' },
    { label: 'Car Insurance', to: '/insurance/car' },
    { label: 'Bike Insurance', to: '/insurance/bike' },
    { label: 'Life Insurance', to: '/insurance/life' },
    { label: 'Term Insurance', to: '/insurance/term' },
    { label: 'Group Term Insurance', to: '/products/group-term' },
    { label: 'Group Health Insurance', to: '/products/group-health' },
    { label: 'Personal Accident Insurance', to: '/products/personal-accident' },
    { label: 'Home Insurance', to: '/products/home-insurance' },
    { label: 'Workmen Compensation Insurance', to: '/products/workmen-compensation' },
    { label: 'Fire and Burglary Insurance', to: '/products/fire-burglary' },
    { label: 'Marine Insurance', to: '/products/marine-insurance' },
    { label: 'HDFC Life Insurance', to: '/products/hdfc-life' },
    { label: 'Care Health Insurance', to: '/products/care-health' },
  ],
  Company: [
    { label: 'About Us', to: '/' },
    { label: 'Our Services', to: '/' },
    { label: 'Contact Us', to: '/#contact' },
    { label: 'Blog', to: '/' },
  ],

};

const Footer = () => {
  const lastUpdated = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());

  return (
    <footer className="bg-[#0A1E37] text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="w-full lg:max-w-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="relative">
                <div className="absolute -inset-1 rounded-2xl bg-emerald-400/30 blur-md" />
                <div className="relative w-12 h-12 rounded-2xl bg-white ring-2 ring-white/30 overflow-hidden">
                  <img
                    src="/insure_logo.jpeg"
                    alt="InsureRiskFree Solutions"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="leading-tight">
                <div className="text-lg font-extrabold text-white">
                  InsureRiskFree <span className="text-[#00B48E]">Solutions</span>
                </div>
                <div className="text-[11px] font-medium text-slate-400 tracking-wide">
                  Insurance • Investment • Insight
                </div>
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-5 max-w-sm">
              We cover what matters the most to you — from life, health, motor, and term plans to group, personal accident, home, workmen compensation, fire, burglary, and marine coverage.
            </p>
            <div className="space-y-2 text-sm">
              {contactInfo.phones.map((p) => (
                <a key={p.number} href={`tel:${p.number.replace(/\s/g, '')}`} className="flex items-center gap-3 hover:text-[#00B48E] transition-colors">
                  <Phone className="w-4 h-4 text-[#00B48E]" /> {p.number} <span className="text-slate-500">• {p.label}</span>
                </a>
              ))}
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 hover:text-[#00B48E] transition-colors">
                <Mail className="w-4 h-4 text-[#00B48E]" /> {contactInfo.email}
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#00B48E] mt-0.5 shrink-0" />
                <span className="leading-relaxed">
                  {contactInfo.address.line2}<br />
                  {contactInfo.address.line3}<br />
                  {contactInfo.address.city}
                </span>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              {[
                { Icon: Facebook, href: contactInfo.socials.facebook },
                { Icon: Twitter, href: contactInfo.socials.twitter },
                { Icon: Linkedin, href: contactInfo.socials.linkedin },
                { Icon: Instagram, href: contactInfo.socials.instagram },
                { Icon: Youtube, href: contactInfo.socials.youtube },
              ].map(({ Icon, href }, i) => (
                <a key={i} href={href} className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#00B48E] flex items-center justify-center transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="w-full lg:max-w-2xl">
            {Object.entries(footerSections).map(([section, items]) => (
              <div key={section} className="mb-8 last:mb-0">
                <h4 className="font-semibold text-white mb-4">{section}</h4>
                <div className="grid gap-2 sm:grid-cols-2">
                  <ul className="space-y-2.5">
                    {items.slice(0, Math.ceil(items.length / 2)).map((item) => (
                      <li key={item.label}>
                        <Link to={item.to} className="text-sm text-slate-400 hover:text-[#00B48E] transition-colors">{item.label}</Link>
                      </li>
                    ))}
                  </ul>
                  <ul className="space-y-2.5">
                    {items.slice(Math.ceil(items.length / 2)).map((item) => (
                      <li key={item.label}>
                        <Link to={item.to} className="text-sm text-slate-400 hover:text-[#00B48E] transition-colors">{item.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div>© 2026 InsureRiskFree Solutions | Insurance • Investment • Insight | All rights reserved</div>
          <div className="flex flex-wrap items-center gap-5">
            <span>Last updated: {lastUpdated}</span>
            <span>IRDAI Licensed Advisor</span>
            <span>Developed by Sharath Gangadhar</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
